import { type, type Type } from 'arktype';
import { blockedWindowRequestResultSchema, type BlockedWindowRequestResult } from './blocked-window.model';
import { readEnvelopeShape, type ReadEnvelope } from './read-envelope.model';
import { narrowError } from './schema-narrow';
import { utcInstantSchema } from './utc-instant.model';

export interface ClockInInput {
  startTime?: string;
  homeOffice?: boolean;
  note?: string | null;
}

export const clockInSchema: Type<ClockInInput> = type({
  'startTime?': utcInstantSchema,
  'homeOffice?': 'boolean',
  'note?': 'string | null',
});

export interface RetroactiveClockOutInput {
  endTime: string;
}

export const retroactiveClockOutSchema: Type<RetroactiveClockOutInput> = type({ endTime: utcInstantSchema }).onUndeclaredKey('reject');

export type ClockStampType = 'in' | 'out';

export interface ClockLastStamp {
  type: ClockStampType;
  at: string;
}

export const clockStateValues = ['in', 'out', 'pause'] as const;
export type ClockStateValue = (typeof clockStateValues)[number];

export interface ClockState extends ReadEnvelope {
  state: ClockStateValue;
  isClockedIn: boolean;
  openStamp: { bookingId: string; startedAt: string } | null;
  lastStamp: ClockLastStamp | null;
  firstIn: string | null;
  since: string | null;
  stale: boolean;
  dayLocked: boolean;
  nextStampAllowedAt: string | null;
}

export interface ClockStampResponse extends ClockState {
  ignored: boolean;
  blockedWindowRequest?: BlockedWindowRequestResult;
}

// An open stamp is a booking_records row with endTime null + metadata.openStamp = true — no dedicated table.
const clockStateShape = {
  state: type.enumerated(...clockStateValues),
  isClockedIn: 'boolean',
  openStamp: type({ bookingId: 'string > 0', startedAt: utcInstantSchema }).or('null'),
  lastStamp: type({ type: "'in' | 'out'", at: utcInstantSchema }).onUndeclaredKey('reject').or('null'),
  firstIn: utcInstantSchema.or('null'),
  since: utcInstantSchema.or('null'),
  stale: 'boolean',
  dayLocked: 'boolean',
  nextStampAllowedAt: utcInstantSchema.or('null'),
  ...readEnvelopeShape,
} as const;

function clockStateError(clock: ClockState): string | null {
  const clockedIn = clock.state === 'in';
  if (clock.isClockedIn !== clockedIn) return 'isClockedIn exactly in the in state';
  if ((clock.openStamp !== null) !== clockedIn) return 'an open stamp exactly in the in state';
  if (clock.stale && !clockedIn) return 'a stale stamp only in the in state';
  if ((clock.firstIn === null) !== (clock.since === null)) return 'firstIn together with since';
  if (clock.state !== 'out' && clock.firstIn === null) return 'firstIn and since while clocked in or on a break';
  return clock.openStamp !== null && Date.parse(clock.openStamp.startedAt) !== Date.parse(clock.since ?? '')
    ? 'since equal to the open stamp start'
    : null;
}

export const clockStateSchema: Type<ClockState> = type(clockStateShape).narrow(narrowError(clockStateError)).onDeepUndeclaredKey('reject');

export const clockStampResponseSchema: Type<ClockStampResponse> = type({
  ...clockStateShape,
  ignored: 'boolean',
  'blockedWindowRequest?': blockedWindowRequestResultSchema,
})
  .narrow(narrowError(clockStateError))
  .onDeepUndeclaredKey('reject');

export function isStampGuarded(nextStampAllowedAt: string | null, nowMs: number): boolean {
  return nextStampAllowedAt !== null && Date.parse(nextStampAllowedAt) > nowMs;
}
